/**
 * The people list on /affiliates: a table on wide screens, cards on a phone.
 * Relationship, channel and status change right in the row.
 */
import { Link, useLocation } from "react-router-dom";
import { EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar } from "@/components/ui";
import { num, usd } from "@/features/command/ui";
import {
  CHANNELS, CHANNEL_COLOR, CHANNEL_FALLBACK, KINDS, KIND_META, PillSelect, STATUSES, STATUS_META, TONE_CLASS,
  dueOf, kindLabel, memberName,
} from "./shared";
import type { AffiliateChannel, AffiliateKind, AffiliateRow, AffiliateStatus, Rhythm } from "./types";

export type RowPatch = Partial<{ kind: AffiliateKind; channel: AffiliateChannel; status: AffiliateStatus }>;

/** Active codes first (the platform keeps old ones switched off). */
export function orderedCodes(a: Pick<AffiliateRow, "codes" | "program">): Array<{ code: string; off: boolean }> {
  const off = new Set((a.program?.codes ?? []).filter((c) => c.disabled).map((c) => c.code.toLowerCase()));
  return [...a.codes]
    .map((code) => ({ code, off: off.has(code.toLowerCase()) }))
    .sort((x, y) => Number(x.off) - Number(y.off));
}

function Selectors({ a, rhythm, channels, onPatch, which }: {
  a: AffiliateRow;
  rhythm?: Partial<Record<string, Rhythm>> | null;
  channels?: Partial<Record<string, string>> | null;
  onPatch: (id: string, patch: RowPatch) => void;
  which: "kind" | "channel" | "status";
}) {
  if (which === "kind") {
    return (
      <PillSelect<AffiliateKind>
        value={a.kind}
        color={KIND_META[a.kind]?.color ?? "zinc"}
        title="Relationship"
        options={KINDS.map((k) => ({ value: k, label: kindLabel(k, rhythm) }))}
        onChange={(v) => onPatch(a.id, { kind: v })}
      />
    );
  }
  if (which === "channel") {
    return (
      <PillSelect<AffiliateChannel>
        value={a.channel}
        color={CHANNEL_COLOR[a.channel] ?? "zinc"}
        title="Where they sell us"
        options={CHANNELS.map((c) => ({ value: c, label: channels?.[c] ?? CHANNEL_FALLBACK[c] }))}
        onChange={(v) => onPatch(a.id, { channel: v })}
      />
    );
  }
  return (
    <PillSelect<AffiliateStatus>
      value={a.status}
      color={STATUS_META[a.status]?.color ?? "zinc"}
      title="Status"
      options={STATUSES.map((s) => ({ value: s, label: STATUS_META[s].label }))}
      onChange={(v) => onPatch(a.id, { status: v })}
    />
  );
}

function Codes({ a }: { a: AffiliateRow }) {
  const codes = orderedCodes(a);
  if (!codes.length) return <span className="text-zinc-600">none</span>;
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn("rounded-md bg-zinc-800/80 px-1.5 py-0.5 font-mono text-[11px] text-zinc-200", codes[0]!.off && "text-zinc-500 line-through")}>{codes[0]!.code}</span>
      {codes.length > 1 && <span className="text-[11px] text-zinc-500" title={codes.slice(1).map((c) => c.code).join(", ")}>+{codes.length - 1}</span>}
    </span>
  );
}

function NextMove({ a }: { a: AffiliateRow }) {
  if (!a.next_move_label) return <span className="text-zinc-600">nothing planned</span>;
  const d = dueOf(a.next_move_date);
  return (
    <span className="block min-w-0">
      <span className="block truncate text-zinc-300">{a.next_move_label}</span>
      <span className={cn("text-[11px] font-medium", TONE_CLASS[d.tone])}>{d.text}</span>
    </span>
  );
}

function NameCell({ a, from }: { a: AffiliateRow; from: string }) {
  return (
    <Link to={`/affiliates/${a.id}`} state={{ from }} className="group flex min-w-0 items-center gap-2.5">
      <Avatar name={a.name} email={a.email} size="sm" />
      <span className="min-w-0">
        <span className="flex items-center gap-1.5">
          <span className="truncate font-medium text-zinc-100 group-hover:text-indigo-300">{a.name}</span>
          {a.hidden && <EyeOff className="h-3 w-3 shrink-0 text-zinc-500" aria-label="Hidden" />}
        </span>
        {a.email && <span className="block truncate text-[11px] text-zinc-500">{a.email}</span>}
      </span>
    </Link>
  );
}

export function PeopleList({
  rows, rhythm, channels, onPatch,
}: {
  rows: AffiliateRow[];
  rhythm?: Partial<Record<string, Rhythm>> | null;
  channels?: Partial<Record<string, string>> | null;
  onPatch: (id: string, patch: RowPatch) => void;
}) {
  const { pathname, search } = useLocation();
  const from = pathname + search;
  const sel = (a: AffiliateRow, which: "kind" | "channel" | "status") => (
    <Selectors a={a} rhythm={rhythm} channels={channels} onPatch={onPatch} which={which} />
  );

  return (
    <>
      {/* Wide screens: a table that scrolls inside its own box if it must. */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[980px] text-sm">
          <thead>
            <tr className="border-b border-zinc-800/70 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
              <th className="px-4 py-2.5 font-semibold">Name</th>
              <th className="px-2 py-2.5 font-semibold">Relationship</th>
              <th className="px-2 py-2.5 font-semibold">Channel</th>
              <th className="px-2 py-2.5 font-semibold">Status</th>
              <th className="px-2 py-2.5 font-semibold">Code</th>
              <th className="px-2 py-2.5 text-right font-semibold">Referred</th>
              <th className="px-2 py-2.5 text-right font-semibold">Earned</th>
              <th className="px-2 py-2.5 text-right font-semibold">Owed</th>
              <th className="px-3 py-2.5 font-semibold">Next move</th>
              <th className="px-4 py-2.5 font-semibold">Owner</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/50">
            {rows.map((a) => {
              const p = a.program ?? {};
              return (
                <tr key={a.id} className={cn("transition-colors hover:bg-zinc-800/25", (a.hidden || a.status === "ended") && "opacity-60")}>
                  <td className="max-w-[240px] px-4 py-2.5"><NameCell a={a} from={from} /></td>
                  <td className="px-2 py-2.5">{sel(a, "kind")}</td>
                  <td className="px-2 py-2.5">
                    {sel(a, "channel")}
                    {a.channel_note && <div className="mt-0.5 max-w-[140px] truncate text-[11px] text-zinc-500" title={a.channel_note}>{a.channel_note}</div>}
                  </td>
                  <td className="px-2 py-2.5">{sel(a, "status")}</td>
                  <td className="px-2 py-2.5"><Codes a={a} /></td>
                  <td className="px-2 py-2.5 text-right tabular-nums text-zinc-200">{num(p.referred ?? 0)}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums text-zinc-300">{usd(p.gross_usd ?? 0)}</td>
                  <td className={cn("px-2 py-2.5 text-right tabular-nums", Number(p.due_usd ?? 0) > 0 ? "font-semibold text-amber-300" : "text-zinc-500")}>{usd(p.due_usd ?? 0)}</td>
                  <td className="max-w-[200px] px-3 py-2.5 text-[13px]"><NextMove a={a} /></td>
                  <td className="px-4 py-2.5 text-[13px] text-zinc-400">{a.owner_name ? memberName(a.owner_name).split(/\s+/)[0] : <span className="text-zinc-600">nobody</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Phone: one card per person. */}
      <div className="divide-y divide-zinc-800/60 md:hidden">
        {rows.map((a) => {
          const p = a.program ?? {};
          return (
            <div key={a.id} className={cn("space-y-2.5 px-4 py-3.5", (a.hidden || a.status === "ended") && "opacity-60")}>
              <div className="flex items-start justify-between gap-3">
                <NameCell a={a} from={from} />
                <Codes a={a} />
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {sel(a, "kind")}
                {sel(a, "channel")}
                {sel(a, "status")}
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-zinc-900/50 px-2 py-1.5">
                  <div className="text-sm font-semibold tabular-nums text-zinc-100">{num(p.referred ?? 0)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Referred</div>
                </div>
                <div className="rounded-lg bg-zinc-900/50 px-2 py-1.5">
                  <div className="text-sm font-semibold tabular-nums text-zinc-100">{usd(p.gross_usd ?? 0)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Earned</div>
                </div>
                <div className="rounded-lg bg-zinc-900/50 px-2 py-1.5">
                  <div className={cn("text-sm font-semibold tabular-nums", Number(p.due_usd ?? 0) > 0 ? "text-amber-300" : "text-zinc-100")}>{usd(p.due_usd ?? 0)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Owed</div>
                </div>
              </div>
              <div className="flex items-end justify-between gap-3 text-[13px]">
                <div className="min-w-0"><NextMove a={a} /></div>
                <div className="shrink-0 text-xs text-zinc-500">{a.owner_name ? memberName(a.owner_name).split(/\s+/)[0] : "No owner"}</div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
